import { KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { dashboardPathForRole } from '../utils/loginRedirect';

const SIGN_IN_JSON = '/api/v1/auth/sign_in';
const VERIFY_OTP_JSON = '/api/v1/auth/verify_otp';
const FRONTEND_ROOT =
  typeof window !== 'undefined' ? window.location.origin : (import.meta.env.VITE_APP_URL || 'http://localhost:5173');

type LoginStep = 'credentials' | 'otp' | '2fa_setup';

function jsonErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  for (const key of ['error', 'message'] as const) {
    const v = o[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function friendlyNetworkMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'La conexión tardó demasiado. Intentá nuevamente.';
  }
  return 'No pudimos conectarnos en este momento. Revisá tu conexión e intentá otra vez.';
}

function looksLikeBackendUnavailable(
  msg: string | null | undefined,
  rawText: string | null | undefined,
): boolean {
  const haystack = `${msg || ''} ${rawText || ''}`.toLowerCase();
  return (
    haystack.includes('econnrefused') ||
    haystack.includes('proxy error') ||
    haystack.includes('failed to fetch') ||
    haystack.includes('connection refused')
  );
}

function friendlyLoginHttpMessage(status: number, serverMsg: string | null, rawText?: string): string {
  if (status === 401 || status === 422) return serverMsg || 'Email o contraseña incorrectos.';
  if (status === 403) return 'Tu cuenta no tiene permisos para iniciar sesión en este módulo.';
  if (status === 404) return 'No pudimos iniciar sesión en este momento. Intentá nuevamente en unos minutos.';
  if (status === 423) return 'Tu cuenta está temporalmente bloqueada. Intentá más tarde.';
  if (status === 429) return 'Hiciste demasiados intentos. Esperá unos minutos y volvé a intentar.';
  if (status >= 500 && looksLikeBackendUnavailable(serverMsg, rawText)) {
    return 'El servicio de inicio de sesión no está disponible por unos momentos. Intentá nuevamente en unos segundos.';
  }
  if (status >= 500) return 'Estamos teniendo un problema temporal. Intentá nuevamente en unos minutos.';
  return serverMsg || 'No se pudo iniciar sesión. Intentá nuevamente.';
}

function friendlyOtpHttpMessage(status: number, serverMsg: string | null, rawText?: string): string {
  if (status === 401) return serverMsg || 'Código inválido. Intentá nuevamente.';
  if (status === 403) return 'No fue posible validar tu código. Solicitá uno nuevo e intentá otra vez.';
  if (status === 404) return 'No pudimos validar tu código en este momento. Intentá nuevamente.';
  if (status === 429) return 'Hiciste demasiados intentos. Esperá unos minutos y volvé a intentar.';
  if (status >= 500 && looksLikeBackendUnavailable(serverMsg, rawText)) {
    return 'El servicio de verificación no está disponible por unos momentos. Intentá nuevamente en unos segundos.';
  }
  if (status >= 500) return 'Estamos teniendo un problema temporal. Intentá nuevamente en unos minutos.';
  return serverMsg || 'No pudimos verificar el código. Intentá nuevamente.';
}

export function LoginPage({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [step, setStep] = useState<LoginStep>('credentials');
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');

  const redirectToDashboard = async () => {
    try {
      const res = await fetch('/api/v1/me', { credentials: 'include' });
      if (res.ok) {
        const user = await res.json();

        // Check if 2FA setup is required before redirecting to dashboard
        if (user.two_factor?.setup_required) {
          window.location.href = `${FRONTEND_ROOT}/2fa-setup`;
          return;
        }

        const path = dashboardPathForRole(user.admin_level, user.team_role);
        window.location.href = `${FRONTEND_ROOT}${path}`;
        return;
      }
    } catch { /* fall through */ }
    window.location.href = `${FRONTEND_ROOT}/crm`;
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(SIGN_IN_JSON, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      if (res.ok) {
        const data = await res.json();

        // User has 2FA enabled — need OTP
        if (data.requires_2fa) {
          setTwoFactorToken(data.two_factor_token);
          setStep('otp');
          setLoading(false);
          return;
        }

        // User needs to set up 2FA (company policy)
        if (data.requires_2fa_setup) {
          window.location.href = `${FRONTEND_ROOT}/2fa-setup`;
          return;
        }

        redirectToDashboard();
        return;
      }

      const rawText = await res.clone().text().catch(() => '');
      const data = await res.json().catch(() => ({}));
      const serverMsg = jsonErrorMessage(data);

      console.error('[LoginPage] sign_in failed', {
        status: res.status,
        serverMsg,
        body: data,
        rawText,
      });
      setError(friendlyLoginHttpMessage(res.status, serverMsg, rawText));
    } catch (error) {
      console.error('[LoginPage] sign_in network error', error);
      setError(friendlyNetworkMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(VERIFY_OTP_JSON, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ two_factor_token: twoFactorToken, otp_code: otpCode.trim() }),
      });

      if (res.ok) {
        const data = await res.json();

        // Backup code login — backend already reset otp_secret, force re-setup
        if (data.requires_2fa_setup) {
          window.location.href = `${FRONTEND_ROOT}/2fa-setup`;
          return;
        }

        redirectToDashboard();
        return;
      }

      const rawText = await res.clone().text().catch(() => '');
      const data = await res.json().catch(() => ({}));
      const serverMsg = jsonErrorMessage(data);

      if (res.status === 401) {
        setError(serverMsg || 'Código inválido. Intentá nuevamente.');
        if (serverMsg?.includes('expiró') || serverMsg?.includes('inválido')) {
          // Token expired — go back to credentials
          setStep('credentials');
          setTwoFactorToken(null);
          setOtpCode('');
        }
        return;
      }

      console.error('[LoginPage] verify_otp failed', {
        status: res.status,
        serverMsg,
        body: data,
        rawText,
      });
      setError(friendlyOtpHttpMessage(res.status, serverMsg, rawText));
    } catch (error) {
      console.error('[LoginPage] verify_otp network error', error);
      setError(friendlyNetworkMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setTwoFactorToken(null);
    setOtpCode('');
    setError(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg p-4">
      <div className="w-full max-w-[24rem] flex flex-col items-center">
        {step === 'credentials' && (
          <>
            <h1 className="text-4xl font-bold text-zinc-900">Iniciar sesión</h1>
            <p className="text-zinc-500 mt-2 mb-6">¡Bienvenido!</p>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="w-full mb-4 px-4 py-3 rounded-lg bg-red-100 border border-red-500 text-red-700 text-sm"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleCredentialsSubmit} className="w-full space-y-4" data-testid="login-form">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">
                  <Mail className="h-5 w-5" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  autoComplete="email"
                  data-testid="login-email"
                  className="w-full py-3 pl-10 pr-4 mt-1 border border-zinc-300 rounded-3xl bg-white text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">
                  <Lock className="h-5 w-5" />
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="w-full py-3 pl-10 pr-4 mt-1 border border-zinc-300 rounded-3xl bg-white text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full mt-5 px-4 py-3 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Ingresando...' : 'Ingresar'}
              </button>
            </form>

            {onNavigate && (
              <button
                type="button"
                onClick={() => onNavigate('forgot')}
                data-testid="login-forgot-password"
                className="mt-4 text-sm text-violet-600 hover:text-violet-700 transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            )}
          </>
        )}

        {step === 'otp' && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
              <ShieldCheck className="h-7 w-7 text-violet-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">Verificación 2FA</h1>
            <p className="text-zinc-500 mt-2 mb-6 text-center text-sm">
              Ingresá el código de 6 dígitos de tu aplicación de autenticación o un código de recuperación.
            </p>

            {error && (
              <div
                role="alert"
                className="w-full mb-4 px-4 py-3 rounded-lg bg-red-100 border border-red-500 text-red-700 text-sm"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleOtpSubmit} className="w-full space-y-4">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">
                  <KeyRound className="h-5 w-5" />
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\s/g, ''))}
                  placeholder="Código de verificación"
                  required
                  autoFocus
                  maxLength={20}
                  data-testid="login-otp"
                  className="w-full py-3 pl-10 pr-4 mt-1 border border-zinc-300 rounded-3xl bg-white text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-center tracking-widest text-lg font-mono"
                />
              </div>
              <button
                type="submit"
                disabled={loading || otpCode.length < 6}
                data-testid="login-otp-submit"
                className="w-full mt-5 px-4 py-3 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Verificando...' : 'Verificar'}
              </button>
            </form>

            <button
              type="button"
              onClick={handleBackToCredentials}
              className="mt-4 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
            >
              Volver al inicio de sesión
            </button>
          </>
        )}
      </div>
    </div>
  );
}
